import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, ProtectedRoute, AuthOverlay, SuspendedScreen } from './context/AuthContext';
import Movers from './pages/Movers';
import Brands from './pages/Brands';
import BrandDetail from './pages/BrandDetail';
import Login from './pages/Login';
import PendingReview from './pages/PendingReview';
import Following from './pages/Following';
import Tradeshow from './pages/Tradeshow';
import Chat from './pages/Chat';
import Settings from './pages/Settings';
import InstallPrompt from './components/pwa/InstallPrompt';
import UpdateBanner from './components/pwa/UpdateBanner';
import './App.css';
import './styles/pwa.css';

// Main app routes with auth protection
function AppRoutes() {
  const { isAuthenticated, loading, redirecting, suspended, logout } = useAuth();

  // Hub redirect in flight — keep the overlay up until the browser
  // actually navigates away. Without this, the SPA briefly renders the
  // local Login page (or, on logout, the dashboard goes blank then
  // flips to /login) before window.location.href takes effect.
  if (redirecting) {
    return <AuthOverlay phase={redirecting} />;
  }
  if (suspended) {
    return (
      <SuspendedScreen
        orgId={suspended.orgId}
        message={suspended.message}
        onLogout={logout}
      />
    );
  }
  if (loading) {
    return <AuthOverlay phase="loading" />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Movers />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/brands" 
        element={
          <ProtectedRoute requiredPermission="view_brands">
            <Brands />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/brands/research/pending"
        element={
          <ProtectedRoute requiredPermission="view_brands">
            <PendingReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brands/:id"
        element={
          <ProtectedRoute requiredPermission="view_brands">
            <BrandDetail />
          </ProtectedRoute>
        }
      />
      {/* Rankings is now a sort mode on Brands. */}
      <Route path="/rankings" element={<Navigate to="/brands" replace />} />
      <Route
        path="/following"
        element={
          <ProtectedRoute requiredPermission="view_brands">
            <Following />
          </ProtectedRoute>
        }
      />
      {/* Desktop only: the page itself shows a notice on a phone and the
          mobile tab bar leaves it out (see OrganicShell). */}
      <Route
        path="/tradeshow"
        element={
          <ProtectedRoute requiredPermission="view_brands">
            <Tradeshow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      {/* Old destinations that Settings absorbed. */}
      <Route path="/settings/focus-score" element={<Navigate to="/settings" replace />} />
      <Route path="/admin/tenants" element={<Navigate to="/settings?tab=organisations" replace />} />
      <Route path="/admin/health" element={<Navigate to="/settings?tab=health" replace />} />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
    </Routes>
  );
}

// Page chrome. Every screen now draws its own full-viewport Organic
// layout and its own footer line — the authenticated ones through
// OrganicShell, the signed-out ones (login, the signing-in overlay, the
// suspended notice) through o-auth. So there is no padded wrapper or
// global footer left to apply.
function AppShell() {
  return (
    <div className="app">
      <main>
        <AppRoutes />
      </main>

      {/* Install / update chrome floats over every screen; the stack keeps
          the two from landing on top of each other when both are up. */}
      <div className="pwa-stack">
        <UpdateBanner />
        <InstallPrompt />
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppShell />
      </Router>
    </AuthProvider>
  );
}

export default App;
